import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/pagination';
import { Children, forwardRef, useMemo } from 'react';
import SwiperCore, { Autoplay, FreeMode, Pagination } from 'swiper';
import { Swiper, SwiperSlide, type SwiperRef } from 'swiper/react';

import { useWindow } from '@src/contexts/window-context';
import { useDesignToken } from '@src/hooks/use-design-token';
import { useIsMobile } from '@src/hooks/use-is-mobile';

import { NavigationButtons } from './navigation-arrows';
import { Wrapper } from './styles';
import { SliderProps as SwiperSliderProps } from './types';

// oxlint-disable-next-line react-hooks/rules-of-hooks -- Swiper's static plugin registration, not React's `use` hook
SwiperCore.use([Pagination, FreeMode, Autoplay]);

interface SliderProps extends SwiperSliderProps {
  paginationBulletsPosition?: 'top' | 'bottom' | 'in-image';
  useNavigationArrows?: boolean;
  useInfiniteSlider?: boolean;
  grabCursor?: boolean;
  className?: string;
}

const Slider = forwardRef<SwiperRef, SliderProps>(
  (
    {
      children,
      paginationBulletsPosition = 'bottom',
      useNavigationArrows = false,
      useInfiniteSlider = false,
      grabCursor = true,
      className,
      ...options
    }: SliderProps,
    ref,
  ) => {
    const { width } = useWindow();
    const slides = useMemo(() => Children.map(children, (child) => <SwiperSlide>{child}</SwiperSlide>), [children]);
    const gridGutter = useDesignToken('--grid-gutter', 'number') as number | undefined;

    const slidesPerViewDefault = useIsMobile() ? 1 : 2;
    const loop = options.loop ?? true;

    if (!width) {
      return <></>;
    }

    const swiperPaginationFlexDir = paginationBulletsPosition === 'top' ? 'column-reverse' : 'column';

    return (
      <Wrapper
        $bulletsInImage={paginationBulletsPosition === 'in-image'}
        $swiperPaginationFlexDir={swiperPaginationFlexDir}
        $infiniteSlider={useInfiniteSlider}
        className={className}
      >
        <Swiper
          ref={ref}
          slidesPerView={options.slidesPerView ?? slidesPerViewDefault}
          spaceBetween={gridGutter}
          pagination={{
            clickable: true,
          }}
          loop={loop}
          roundLengths
          grabCursor={grabCursor}
          className={className}
          {...options}
        >
          {slides}
          {useNavigationArrows && <NavigationButtons />}
        </Swiper>
      </Wrapper>
    );
  },
);

export default Slider;
